// src/pages/Level-9.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import LevelTemplate from "./LevelTemplate";
import { STREAM_VIDEOS } from "../videos/streamVideos";
import { TrophyStorage } from "../services/trophyStorage";

/* ─────────────────────────────────────────────
   CONFIG – Level 9 (V1.1) — Row Row Row Your Boat
   - Scene video first, then melody page
   - Cloudflare Stream
   - Uses Crocodile Hero image
   ───────────────────────────────────────────── */

// ⬇️ Fallback Stream ID (from your screenshot)
const LEVEL9_FALLBACK_STREAM_ID = "1e6e779318ac1b668754fe03b4850846";

// Optional: if you later add this to src/videos/streamVideos.js
// STREAM_VIDEOS.L9_CROC_INTRO = { id: "...", title: "Level 9 - Crocodile (Intro)" }
const LEVEL9_STREAM_ID =
  STREAM_VIDEOS?.L9_CROC_INTRO?.id || LEVEL9_FALLBACK_STREAM_ID;

// ✅ LevelTemplate supports: videoSrc="stream:<id>"
const LEVEL9_VIDEO_SRC = `stream:${LEVEL9_STREAM_ID}`;

// ✅ Hero image (put this inside: /public/images/)
const LEVEL9_HERO_IMAGE = "/images/" + encodeURIComponent("Croc.png");

/* ─────────────────────────────────────────────
   MELODY — Row, Row, Row Your Boat
   ✅ FIX: Replace B4 with plain "B" (virtual piano glitch workaround)
   ───────────────────────────────────────────── */

const LEVEL9_MELODY_LINES = [
  {
    id: 0,
    notes: ["E", "E", "E", "F#", "G#"],
    lyric: "Row, row, row your boat",
  },
  {
    id: 1,
    notes: ["G#", "F#", "G#", "A", "B"],
    lyric: "Gently down the stream",
  },
  {
    id: 2,
    // ✅ changed B4 -> B (x3)
    notes: ["E5", "E5", "E5", "B", "B", "B", "G#", "G#", "G#", "E", "E", "E"],
    lyric: "Merrily, merrily, merrily, merrily",
  },
  {
    id: 3,
    // ✅ changed B4 -> B
    notes: ["B", "A", "G#", "F#", "E"],
    lyric: "Life is but a dream",
  },
];

// Words/syllables shown under the notes (simple + kid-friendly)
const LEVEL9_LYRIC_GROUPS = [
  ["Row", "row", "row", "your", "boat"],
  ["Gen-tly", "down", "the", "stream"],
  ["Mer-ri-ly", "Mer-ri-ly", "Mer-ri-ly", "Mer-ri-ly"],
  ["Life", "is", "but", "a", "dream"],
];

// Which notes belong to which lyric group (indexes within each line’s notes array)
const LEVEL9_NOTE_GROUP_INDEXES = [
  // Line 0: 5 notes -> 5 groups (1 note each)
  [
    { start: 0, end: 0 },
    { start: 1, end: 1 },
    { start: 2, end: 2 },
    { start: 3, end: 3 },
    { start: 4, end: 4 },
  ],
  // Line 1: 5 notes -> 4 groups (first group spans 2 notes)
  [
    { start: 0, end: 1 }, // Gen-tly
    { start: 2, end: 2 }, // down
    { start: 3, end: 3 }, // the
    { start: 4, end: 4 }, // stream
  ],
  // Line 2: 12 notes -> 4 groups (3 notes per "Merrily")
  [
    { start: 0, end: 2 },
    { start: 3, end: 5 },
    { start: 6, end: 8 },
    { start: 9, end: 11 },
  ],
  // Line 3: 5 notes -> 5 groups (1 note each)
  [
    { start: 0, end: 0 },
    { start: 1, end: 1 },
    { start: 2, end: 2 },
    { start: 3, end: 3 },
    { start: 4, end: 4 },
  ],
];

/* ─────────────────────────────────────────────
   HERO — Emoji bursts (float + fade + auto-remove)
   - correct: 🐊
   - wrong: 💦
   ───────────────────────────────────────────── */

function Level9Hero({ backgroundImage, noteEvent }) {
  const [bursts, setBursts] = useState([]);
  const idRef = useRef(1);

  // Track timeouts so we can clear them on unmount only (NOT on every note)
  const timeoutsRef = useRef([]);

  const safeRand = useMemo(() => {
    const seed = Math.random() * 9999;
    return () => {
      const x = Math.sin(seed + idRef.current * 12.9898) * 43758.5453;
      return x - Math.floor(x);
    };
  }, []);

  useEffect(() => {
    // cleanup only on unmount
    return () => {
      timeoutsRef.current.forEach((t) => clearTimeout(t));
      timeoutsRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!noteEvent) return;

    const isHit = noteEvent === "hit";
    const emoji = isHit ? "🐊" : "💦";

    const id = idRef.current++;
    const left = 18 + safeRand() * 64;
    const top = 18 + safeRand() * 52;

    // slight sideways drift (-14px..+14px)
    const dx = Math.round((safeRand() * 28 - 14) * 10) / 10;

    setBursts((prev) => [...prev, { id, emoji, left, top, isHit, dx }]);

    // Remove after animation finishes
    const t = setTimeout(() => {
      setBursts((prev) => prev.filter((b) => b.id !== id));
    }, 900);

    timeoutsRef.current.push(t);
  }, [noteEvent, safeRand]);

  return (
    <div className="relative w-full max-w-3xl mx-auto rounded-[22px] sm:rounded-[24px] shadow bg-slate-900/5 overflow-hidden">
      <img
        src={backgroundImage}
        alt=""
        className="w-full h-56 object-cover"
        draggable={false}
      />

      {bursts.map((b) => (
        <div
          key={b.id}
          className="tt-burst"
          style={{
            left: `${b.left}%`,
            top: `${b.top}%`,
            "--dx": `${b.dx}px`,
          }}
          aria-hidden="true"
        >
          <span
            className={"tt-burst-emoji " + (b.isHit ? "tt-hit" : "tt-miss")}
          >
            {b.emoji}
          </span>
        </div>
      ))}

      <style>{`
        .tt-burst{
          position:absolute;
          transform: translate(-50%, -50%);
          pointer-events:none;
          z-index: 5;
        }
        .tt-burst-emoji{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          font-size: 34px;
          filter: drop-shadow(0 6px 10px rgba(0,0,0,0.25));
          animation: ttFloatUp 900ms cubic-bezier(.2,.9,.2,1) forwards;
          will-change: transform, opacity;
        }
        @keyframes ttFloatUp{
          0%   { transform: translate(0px, 0px) scale(1); opacity: 1; }
          70%  { transform: translate(var(--dx), -26px) scale(1.18); opacity: 1; }
          100% { transform: translate(var(--dx), -46px) scale(1.05); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default function Level9() {
  const navigate = useNavigate();

  return (
    <LevelTemplate
      levelId={9}
      currentLevelForSidebar={9}
      videoSrc={LEVEL9_VIDEO_SRC}
      startLabel="Start Game"
      melodyLines={LEVEL9_MELODY_LINES}
      lyricGroupsPerLine={LEVEL9_LYRIC_GROUPS}
      noteGroupIndexesPerLine={LEVEL9_NOTE_GROUP_INDEXES}
      backgroundImage={LEVEL9_HERO_IMAGE}
      heroImageFit="cover"
      renderHero={(heroProps) => (
        <Level9Hero
          backgroundImage={heroProps.backgroundImage}
          noteEvent={heroProps.noteEvent}
        />
      )}
      completionTitle="Crocodile Star unlocked!"
      completionBody={
        <>
          You sailed through{" "}
          <span className="font-semibold text-[#4F46E5]">
            Row, Row, Row Your Boat
          </span>{" "}
          🚣‍♂️🎶 The crocodiles approve your final trial — collect your{" "}
          <span className="font-semibold text-[#4F46E5]">Crocodile Star</span>{" "}
          and get ready for the final battle!
        </>
      }
      // ✅ SUCCESS TRIGGER (Level-9 completed + star "L9" collected)
      onComplete={() => {
        TrophyStorage.markLevelAndStar(9); // marks level "9" + star "L9"
      }}
      onNextLevel={() => navigate("/level-10")}
    />
  );
}
